import React from "react";
import { objectIsEmpty } from "@arenko-group/arenko-ui/dist/helpers";
import { arenkoChartServices } from "@arenko-group/arenko-ui/dist/theme/ArenkoTheme";
import type {
  ChartPivotRow,
  PivotConfig,
  QueryAnnotations,
  ResultSet,
} from "@cubejs-client/core";
import { addDays, compareAsc, format, isValid, startOfWeek } from "date-fns";
import Highcharts from "highcharts";
import {
  ONE_DAY_IN_MILLISECONDS,
  ONE_HOUR_IN_MILLISECONDS,
  ONE_WEEK_IN_MILLISECONDS,
  THIRTY_MINUTES_IN_MILLISECONDS,
} from "@/constants/date.constants";
import {
  setZoomRange,
  toggleZoom,
} from "@/features/scheduleEnergyRootSlice/scheduleEnergySlice";
import type {
  CustomSeriesOptionsType,
  ScheduledDisabledData,
  TransformedWholesaleObj,
} from "@/pages/Energy";
import Tooltip from "@/pages/Energy/components/Tooltip";
import type { DateLike, DateRange, GenericObject } from "@/types/common";
import type { DispatchFn } from "@/types/data";
import type { Profile } from "@/types/data/schedule.types";
import type {
  SeriesDataArr,
  SparseSeriesDataArr,
  XAxisRangeType,
} from "@/types/view";
import { getDateDiffInDays, getXAxisTitleForRange } from "./date.helpers";
import { renderToHighchartsHtml } from "./renderToHighchartsHtml";

/**
 * Returns the keys from the given annotations whose type is "number".
 *
 * @param annotations - Object containing dimensions and measures.
 * @returns Array of keys with type "number".
 */
const getNumericKeys = (annotations: QueryAnnotations) => {
  const { dimensions, measures } = annotations ?? {};
  const combined = { ...dimensions, ...measures };

  return Object.keys(combined).filter(
    (name) => combined[name].type === "number"
  );
};

/**
 * Generates a chart pivot array from a Cube ResultSet, preserving `null` values for numeric fields.
 *
 * This function pivots the result set data according to the provided configuration,
 * and ensures that any `null` values present in the raw data for numeric fields are
 * retained in the output, mapping raw data rows to their corresponding pivoted rows
 * using time or identifier columns.
 *
 * @param resultSet - The Cube ResultSet to pivot. If `null` or `undefined`, returns an empty array.
 * @param pivotConfig - Optional configuration for pivoting the result set.
 * @returns An array of `ChartPivotRow` objects, with `null` values preserved for numeric fields.
 *
 * @throws If the pivoted data length does not match the raw data length.
 *
 * Output example:
 * ```ts
 * [
 *    { x: '2025-06-29T17:30:00.000', xValues: ['2025-06-29T17:30:00.000'], measure1: 100, measure2: null },
 *    { x: '2025-06-29T18:00:00.000', xValues: ['2025-06-29T18:00:00.000'], measure1: 200, measure2: 50 }
 * ]
 * ```
 */
export const chartPivotPreserveNull = (
  resultSet: ResultSet | null | undefined,
  pivotConfig?: PivotConfig
): ChartPivotRow[] => {
  if (!resultSet) {
    return [];
  }

  const numericFields = getNumericKeys(resultSet.annotation());
  const pivotData = resultSet.chartPivot(pivotConfig);
  const rawData = resultSet.rawData();

  if (pivotData.length !== rawData.length) {
    throw new Error(
      `Pivot and raw data are not the same length. Cannot reconcile fields ${numericFields.join()}`
    );
  }

  return pivotData.map((row, idx) => {
    const raw = rawData[idx];
    const update = { ...row };
    numericFields.forEach((field) => {
      if (field in update && raw[field] === null) {
        update[field] = null;
      }
    });

    return update;
  });
};

const zIndexes = {
  scheduledResultant: 1,
  currentTime: 2,
  disabledAutomation: 3,
  contractableEnergy: 4,
  wholesale: 5,
  imbalance: 5,
  gateClosure: 6,
};

const disabledBlockPattern = {
  pattern: {
    color: "hsla(240, 0%, 30%, 0.5)",
    path: {
      d: "M 0 0 L 10 10 M 9 -1 L 11 1 M -1 9 L 1 11",
      strokeWidth: 1,
    },
    width: 10,
    height: 10,
    opacity: 1,
  },
};

type ChartOptionsProps = {
  zoomRange?: XAxisRangeType;
  selectedRange: DateRange;
  intervalFormat: string;
  dispatch: DispatchFn;
  yMin: number;
  yMax: number;
  toggleVisibility: (name: string, visible: boolean) => void;
  isZoomed: boolean;
  handleClick: () => void;
};

function getAutoTickInterval(from: DateLike, to: DateLike): number {
  const diffDays = getDateDiffInDays(from, to);
  if (diffDays < 2) {
    return THIRTY_MINUTES_IN_MILLISECONDS;
  } else if (diffDays <= 3) {
    return ONE_HOUR_IN_MILLISECONDS;
  } else if (diffDays <= 7) {
    return 2 * ONE_HOUR_IN_MILLISECONDS;
  } else if (diffDays <= 31) {
    return ONE_DAY_IN_MILLISECONDS;
  } else {
    return ONE_WEEK_IN_MILLISECONDS;
  }
}

export const getChartOptions = ({
  zoomRange,
  selectedRange,
  intervalFormat,
  dispatch,
  yMin,
  yMax,
  toggleVisibility,
  handleClick,
}: ChartOptionsProps): Highcharts.Options => {
  return {
    credits: {
      enabled: false,
    },
    chart: {
      alignTicks: false,
      zooming: { type: "x" },
      resetZoomButton: {
        theme: { style: { display: "none" } },
      },
      animation: false,
      ignoreHiddenSeries: false,
      events: {
        click: () => {
          handleClick();
        },
      },
    },
    title: {
      text: "",
    },
    legend: {
      align: "center",
      verticalAlign: "top",
      layout: "horizontal",
      floating: false,
      backgroundColor: "transparent",
    },
    plotOptions: {
      scatter: {
        stickyTracking: true,
        states: { hover: { lineWidthPlus: 0 } },
      },
      series: {
        animation: false,
        events: {
          hide: function () {
            toggleVisibility(this.name, false);
          },
          show: function () {
            toggleVisibility(this.name, true);
          },
        },
        states: {
          inactive: {
            opacity: 1,
          },
        },
      },
    },
    xAxis: [
      {
        type: "datetime",
        min: zoomRange?.min ?? new Date(selectedRange.from).getTime(),
        max: zoomRange?.max ?? new Date(selectedRange.to).getTime(),
        tickInterval: getAutoTickInterval(selectedRange.from, selectedRange.to),
        minorTickInterval: getAutoTickInterval(
          selectedRange.from,
          selectedRange.to
        ),
        tickPixelInterval: 1,
        labels: {
          step: 1,
          formatter: (ctx: Highcharts.AxisLabelsFormatterContextObject) => {
            return Highcharts.dateFormat(intervalFormat, Number(ctx.value));
          },
        },
        title: {
          text: getXAxisTitleForRange(selectedRange.from, selectedRange.to),
        },
        gridLineWidth: 1,
        events: {
          setExtremes: ({ min, max, trigger }) => {
            handleSetExtremes({ min, max, trigger }, dispatch);
          },
        },
      },
    ],
    yAxis: [
      {
        gridLineWidth: 1,
        min: yMin || -10,
        max: yMax || 10,
        tickInterval: yMax <= 20 ? 2 : 10,
        title: {
          text: `Average power (MW)`,
        },
        labels: {
          formatter: (ctx: Highcharts.AxisLabelsFormatterContextObject) => {
            return Highcharts.numberFormat(Number(ctx.value), 0, "", "");
          },
        },
      },
    ],
  };
};

/**
 * Check if currentTime is within the given range
 *
 * @param currentTime
 * @param range
 */
export const isTimeWithinRange = (
  time: DateLike,
  { from, to }: DateRange
): boolean =>
  compareAsc(new Date(time), new Date(from)) === 1 &&
  compareAsc(new Date(to), new Date(time)) === 1;

/**
 * Mutates the series.visible based on visibility state
 */
export const setSeriesVisibility = (
  visibility: GenericObject<boolean>,
  series: CustomSeriesOptionsType
) => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-boolean-literal-compare
  series.visible = Boolean(series.name && visibility[series.name] !== false);
  return series;
};

/**
 * Creates series for markets that need to be stacked on top of one another
 *
 * @param marketVolData
 * @param contractedResultantSeries
 */
export const getStackedColumnSeries = (
  marketVolData: TransformedWholesaleObj[],
  contractedResultantSeries: SeriesDataArr[],
  visibility: GenericObject<boolean> = {}
): (CustomSeriesOptionsType | GenericObject<never>)[] => {
  const emptyObj = {};
  let showWholesale = false,
    showImbalance = false;
  const [imbalanceSeriesData, wholesaleSeriesData] = marketVolData.reduce(
    (acc, data, index) => {
      if (!showWholesale && data.Wholesale !== 0) {
        showWholesale = true;
      }
      if (!showImbalance && data.Imbalance !== 0) {
        showImbalance = true;
      }
      acc[0].push([contractedResultantSeries[index][0], data.Imbalance]);
      acc[1].push([contractedResultantSeries[index][0], data.Wholesale]);
      return acc;
    },
    [[], [], []] as [SeriesDataArr[], SeriesDataArr[], SeriesDataArr[]]
  );
  return [
    showImbalance
      ? setSeriesVisibility(visibility, {
          name: "Imbalance",
          type: "area",
          legendSymbol: "rectangle",
          data: imbalanceSeriesData,
          showInLegend: showImbalance,
          stacking: "normal",
          color: arenkoChartServices.imbalance.backgroundColor,
          fillColor: arenkoChartServices.imbalance.backgroundColor,
          legendIndex: 1,
        })
      : emptyObj,
    showWholesale
      ? setSeriesVisibility(visibility, {
          name: "Wholesale",
          type: "area",
          legendSymbol: "rectangle",
          data: wholesaleSeriesData,
          showInLegend: showWholesale,
          stacking: "normal",
          color: arenkoChartServices.wholesale.backgroundColor,
          fillColor: arenkoChartServices.wholesale.backgroundColor,
          legendIndex: 2,
        })
      : emptyObj,
  ];
};

/**
 * Creates a current-time chart series that displays a vertical line.
 */
export const getCurrentTimeSeries = (
  currentTime: number,
  selectedRange: DateRange,
  yMax: number,
  yMin: number,
  visibility: GenericObject<boolean> = {}
): CustomSeriesOptionsType => {
  const showCurrentTimeSeries = isTimeWithinRange(currentTime, selectedRange);
  return setSeriesVisibility(visibility, {
    name: "Current time",
    type: "line" as const,
    color: "#000000",
    zIndex: zIndexes.currentTime,
    enableMouseTracking: false,
    marker: { enabled: false },
    states: { hover: { enabled: false } },
    data: showCurrentTimeSeries
      ? [
          { x: currentTime, y: yMin - 10 },
          { x: currentTime, y: yMax + 10 },
        ]
      : [],
    legendIndex: 10,
    showInLegend: showCurrentTimeSeries,
  });
};

/**
 * Creates a gate-closure chart series that displays a dashed vertical line.
 */
export const getGateClosureSeries = (
  gateClosure: number,
  selectedRange: DateRange,
  yMax: number,
  yMin: number,
  visibility: GenericObject<boolean> = {}
): CustomSeriesOptionsType => {
  const showGateClosureSeries = isTimeWithinRange(gateClosure, selectedRange);
  return setSeriesVisibility(visibility, {
    name: "gate Closure",
    type: "line",
    color: "#000000",
    dashStyle: "Dash",
    zIndex: zIndexes.gateClosure,
    enableMouseTracking: false,
    marker: { enabled: false },
    states: { hover: { enabled: false } },
    data: showGateClosureSeries
      ? [
          { x: gateClosure, y: yMin - 10 },
          { x: gateClosure, y: yMax + 10 },
        ]
      : [],
    legendIndex: 10,
    showInLegend: false,
  });
};

export type GetChartSeriesProps = {
  contractedResultantCorrespondingMarketVolumeData: TransformedWholesaleObj[];
  correspondingScheduledData: number[];
  contractedResultantSeriesData: SeriesDataArr[];
  scheduledResultantSeriesData: SeriesDataArr[];
  scheduledDisabledProfileSeriesData: SeriesDataArr[];
  correspondingScheduledDisabledData: ScheduledDisabledData[];
  minContractableSeries: SparseSeriesDataArr[];
  maxContractableSeries: SparseSeriesDataArr[];
  currentTime: number | undefined;
  gateClosure: number | undefined;
  isBM: boolean | undefined;
  selectedRange: DateRange;
  yMax: number;
  yMin: number;
  visibility: GenericObject<boolean>;
};

export const getChartSeries = ({
  contractedResultantSeriesData,
  scheduledResultantSeriesData,
  scheduledDisabledProfileSeriesData,
  contractedResultantCorrespondingMarketVolumeData,
  correspondingScheduledData,
  correspondingScheduledDisabledData,
  minContractableSeries,
  maxContractableSeries,
  currentTime,
  gateClosure,
  isBM,
  selectedRange,
  yMax,
  yMin,
  visibility = {},
}: GetChartSeriesProps): { series: Highcharts.SeriesOptions[] } => {
  const series: CustomSeriesOptionsType[] = [];

  if (currentTime) {
    series.push(
      setSeriesVisibility(
        visibility,
        getCurrentTimeSeries(currentTime, selectedRange, yMax, yMin, visibility)
      )
    );
  }

  if (gateClosure && isBM === true) {
    series.push(
      setSeriesVisibility(
        visibility,
        getGateClosureSeries(gateClosure, selectedRange, yMax, yMin, visibility)
      )
    );
  }

  const [imbalanceSeries, wholesaleSeries] = getStackedColumnSeries(
    contractedResultantCorrespondingMarketVolumeData,
    contractedResultantSeriesData,
    visibility
  );
  if (!objectIsEmpty(imbalanceSeries))
    series.push(imbalanceSeries as CustomSeriesOptionsType);

  if (!objectIsEmpty(wholesaleSeries))
    series.push(wholesaleSeries as CustomSeriesOptionsType);

  series.push(
    setSeriesVisibility(visibility, {
      name: "Min Contractable Energy",
      type: "area",
      legendSymbol: "rectangle",
      data: minContractableSeries,
      color: "rgb(207, 106, 156)",
      fillColor: "rgb(225, 165, 195, 0.1)",
      legendIndex: 9,
      enableMouseTracking: false,
    })
  );

  series.push(
    setSeriesVisibility(visibility, {
      name: "Max Contractable Energy",
      type: "area",
      legendSymbol: "rectangle",
      data: maxContractableSeries,
      color: "rgb(26, 127, 127)",
      fillColor: "rgb(138, 186, 186, 0.1)",
      legendIndex: 9,
      enableMouseTracking: false,
    })
  );

  series.push(
    setSeriesVisibility(visibility, {
      name: "Contracted",
      type: "scatter",
      dashStyle: "ShortDash",
      lineWidth: 1.5,
      marker: {
        enabled: true,
        fillColor: "transparent",
      },
      states: {
        hover: {
          enabled: false,
        },
      },
      data: contractedResultantSeriesData,
      color: "#70b96f",
      marketVolumeData: contractedResultantCorrespondingMarketVolumeData,
      scheduledResultantData: correspondingScheduledData,
      scheduledDisabledData: correspondingScheduledDisabledData,
      maxContractablePower: maxContractableSeries,
      minContractablePower: minContractableSeries,
      legendIndex: 9,
    })
  );
  series.push(
    setSeriesVisibility(visibility, {
      name: "Scheduled",
      type: "scatter",
      lineWidth: 1.5,
      marker: {
        enabled: true,
        fillColor: "transparent",
      },
      states: {
        hover: {
          enabled: false,
        },
      },
      enableMouseTracking: false,
      data: scheduledResultantSeriesData,
      color: arenkoChartServices.resultant.backgroundColor,
      legendIndex: 9,
      zIndex: zIndexes.scheduledResultant,
    })
  );

  series.push(
    setSeriesVisibility(visibility, {
      name: "Disabled automation",
      type: "area",
      fillColor: disabledBlockPattern,
      color: disabledBlockPattern,
      lineWidth: 0,
      threshold: null,
      zIndex: zIndexes.disabledAutomation,
      enableMouseTracking: false,
      data: scheduledDisabledProfileSeriesData,
    })
  );

  return { series };
};

export const handleSetExtremes = (
  { min, max, trigger }: Partial<Highcharts.AxisSetExtremesEventObject>,
  dispatch: DispatchFn
) => {
  if (trigger === "zoom") {
    const notReset = min !== undefined && max !== undefined;
    dispatch(toggleZoom(notReset));
    if (notReset) {
      dispatch(setZoomRange({ min, max }));
    }
  }
};

type GetChartTooltipProps = {
  selectedRange: DateRange;
  settlementPeriodInMilliseconds: number;
};

export const getChartTooltip = ({
  selectedRange,
  settlementPeriodInMilliseconds,
}: GetChartTooltipProps) => {
  return {
    tooltip: {
      followPointer: true,
      snap: 50,
      useHTML: true,
      formatter: function (this: Highcharts.Point) {
        const index = this.index;
        const series = this.series;
        const options = series?.options as CustomSeriesOptionsType | undefined;
        if (!options || typeof index !== "number") return false;

        const marketVolumeData = options.marketVolumeData?.[index];
        const scheduledData = options.scheduledResultantData?.[index];
        const scheduledDisabledRange = options.scheduledDisabledData?.[index];
        const maxContractablePower = options.maxContractablePower?.[index]?.[1];
        const minContractablePower = options.minContractablePower?.[index]?.[1];

        const tooltipStringArr: string[] = [];
        if (marketVolumeData && Object.values(marketVolumeData).length) {
          tooltipStringArr.push(
            renderToHighchartsHtml(
              <Tooltip
                point={this}
                selectedRange={selectedRange}
                settlementPeriodInMilliseconds={settlementPeriodInMilliseconds}
                marketVolumeData={marketVolumeData}
                scheduledData={scheduledData}
                scheduledDisabledRange={scheduledDisabledRange}
                maxContractablePower={maxContractablePower}
                minContractablePower={minContractablePower}
              />
            )
          );
        }
        return tooltipStringArr.length > 0 ? tooltipStringArr : false;
      } as Highcharts.TooltipFormatterCallbackFunction,
    },
  };
};

export const extraneousFSPlot = (type: string, abs: Profile[]) =>
  (type.includes("-hf") && abs?.some(({ value }) => Math.sign(value) === 1)) ||
  (type.includes("-lf") && abs?.some(({ value }) => Math.sign(value) === -1));

export const getEmptyScheduleObject = () => {
  return {
    id: "",
    deviceId: "",
    controllerId: "",
    origin: "",
    type: "",
    output: "",
    controlPoint: "",
    createdAt: "",
    state: {
      state: "",
      updatedAt: "",
    },
    lock: {
      locked: false,
      origin: "",
      updatedAt: "",
    },
    profile: [
      {
        time: "",
        value: 0,
      },
    ],
  };
};

export const generateInverterColors: Record<string, string> = {
  Tesla: "rgb(31, 119, 180, 0.8)",
  Nidec: "rgb(227, 119, 194, 0.8)",
  SMA: "rgb(255, 127, 14, 0.8)",
  WSTECH: "rgb(255, 187, 120, 0.8)",
  "Power Electronics": "rgb(148, 103, 189, 0.8)",
  BYD: "rgb(23, 190, 207, 0.8)",
  "General Electric": "rgb(44, 160, 44, 0.8)",
  Sungrow: "rgb(214, 39, 40, 0.8)",
  PE: "rgb(124, 181, 236, 0.8)",
  BMW: "rgb(152, 223, 138, 0.8)",
  CATL: "rgb(255, 99, 71, 0.8)",
  "CATL,Sungrow": "rgb(60, 179, 113, 0.8)",
  GE: "rgb(255, 215, 0, 0.8)",
  "LG Chem": "rgb(255, 20, 147, 0.8)",
  Samsung: "rgb(0, 191, 255, 0.8)",
  Trina: "rgb(106, 90, 205, 0.8)",
};

/**
 * Generates a color string (in HSL format) based on the input value.
 * If `usePredefined` is true, selects a color from a predefined palette using a hash of the input.
 * Otherwise, generates a dynamic HSL color based on the hash.
 *
 * @param value - The value (number, string, undefined, or null) to generate a color for.
 * @param usePredefined - If true, use a predefined set of colors; otherwise, generate a dynamic HSL color. Defaults to true.
 * @returns A color string in HSL format.
 */
export const generateColorFromString = (
  value: number | string | undefined | null,
  usePredefined = true
): string => {
  const predefinedColors = [
    "hsl(0, 85%, 45%)",
    "hsl(100, 90%, 50%)",
    "hsl(200, 80%, 55%)",
    "hsl(300, 75%, 50%)",
    "hsl(20, 90%, 50%)",
    "hsl(120, 80%, 55%)",
    "hsl(220, 75%, 50%)",
    "hsl(340, 90%, 50%)",
    "hsl(40, 80%, 55%)",
    "hsl(140, 75%, 50%)",
    "hsl(240, 85%, 45%)",
    "hsl(10, 80%, 55%)",
    "hsl(60, 75%, 50%)",
    "hsl(160, 85%, 45%)",
    "hsl(260, 90%, 50%)",
    "hsl(30, 75%, 50%)",
    "hsl(80, 85%, 45%)",
    "hsl(180, 90%, 50%)",
    "hsl(280, 80%, 55%)",
    "hsl(50, 85%, 45%)",
    "hsl(90, 80%, 55%)",
    "hsl(190, 75%, 50%)",
    "hsl(300, 75%, 50%)",
    "hsl(70, 90%, 50%)",
    "hsl(110, 75%, 50%)",
    "hsl(210, 85%, 45%)",
    "hsl(320, 85%, 45%)",
    "hsl(130, 85%, 45%)",
    "hsl(150, 90%, 50%)",
    "hsl(230, 90%, 50%)",
  ];

  const hash: number = Array.from(stringOrUnknown(value)).reduce(
    (acc, char) => char.charCodeAt(0) + ((acc << 5) - acc),
    0
  );
  const colorIndex = Math.abs(hash % predefinedColors.length);
  if (usePredefined && colorIndex < predefinedColors.length) {
    return predefinedColors[colorIndex];
  }

  // Fallback to dynamically generating HSL colors
  const h = Math.abs(hash % 360);
  const s = 70;
  const l = 50;
  return `hsl(${h}, ${s}%, ${l}%)`;
};

export const stringOrUnknown = (
  value: number | string | undefined | null,
  fallback = "Unknown"
) => String(value ?? fallback);

type MarketColorScheme = {
  backgroundColor: string;
  borderColor: string;
};

export const marketColors: Record<string, MarketColorScheme> = (() => {
  const colours = {
    bm_bid: {
      backgroundColor: "rgba(16, 46, 66, 0.6)",
      borderColor: "rgba(16, 46, 66, 0.6)",
    },
    bm_non_delivery_charge: {
      backgroundColor: "rgba(19, 93, 96, 0.6)",
      borderColor: "rgba(19, 93, 96, 0.6)",
    },
    bm_offer: {
      backgroundColor: "rgba(102, 51, 153, 0.6)",
      borderColor: "rgba(102, 51, 153, 0.6)",
    },
    dch: {
      backgroundColor: "rgba(0, 128, 160, 0.6)", // more teal/blue
      borderColor: "rgba(0, 128, 160, 0.6)",
    },
    dcl: {
      backgroundColor: "rgba(0, 112, 90, 0.6)", // greener tint
      borderColor: "rgba(0, 112, 90, 0.6)",
    },
    dmh: {
      backgroundColor: "rgba(0, 0, 255, 0.6)",
      borderColor: "rgba(0, 0, 255, 0.6)",
    },
    dml: {
      backgroundColor: "rgba(0, 0, 255, 0.6)",
      borderColor: "rgba(0, 0, 255, 0.6)",
    },
    drh: {
      backgroundColor: "rgba(200, 255, 0, 0.6)",
      borderColor: "rgba(200, 255, 0, 0.6)",
    },
    drl: {
      backgroundColor: "rgba(255, 165, 0, 0.6)",
      borderColor: "rgba(255, 165, 0, 0.6)",
    },
    epex_dam: {
      backgroundColor: "rgba(255, 115, 40, 0.6)",
      borderColor: "rgba(255, 105, 40, 0.6)",
    },
    epex_idm: {
      backgroundColor: "rgba(255, 44, 44, 0.6)",
      borderColor: "rgba(255, 44, 44, 0.6)",
    },
    ffr: {
      backgroundColor: "rgba(0, 255, 0, 0.6)",
      borderColor: "rgba(0, 255, 0, 0.6)",
    },
    imbalance: {
      backgroundColor: "rgba(195, 64, 131, 0.6)",
      borderColor: "rgba(195, 64, 131, 0.6)",
    },
    nbr: {
      backgroundColor: "rgba(85, 202, 182, 0.6)",
      borderColor: "rgba(50, 170, 150, 0.6)",
    },
    nqr: {
      backgroundColor: "rgba(255, 255, 0, 0.6)",
      borderColor: "rgba(204, 204, 0, 0.6)",
    },
    pbr: {
      backgroundColor: "rgba(255, 247, 0, 0.6)",
      borderColor: "rgba(204, 198, 0, 0.6)",
    },
    pqr: {
      backgroundColor: "rgba(16, 46, 66, 0.6)",
      borderColor: "rgba(16, 46, 66, 0.6)",
    },
    wholesale_export: {
      backgroundColor: "rgba(170, 68, 92, 0.6)",
      borderColor: "rgba(150, 50, 100, 0.6)",
    },
    wholesale_import: {
      backgroundColor: "rgba(147, 131, 225, 0.6)",
      borderColor: "rgba(126, 106, 226, 0.6)",
    },
  } as Record<string, MarketColorScheme>;

  // Map existing EPEX colours to the import/export types.
  colours.epex_dam_export = colours.epex_dam;
  colours.epex_dam_import = colours.epex_dam;

  colours.epex_idm_export = colours.epex_idm;
  colours.epex_idm_import = colours.epex_idm;

  return colours;
})();

/**
 * Formats a timestamp to a string representation of the week range the timestamp falls within.
 * @param value
 * @returns
 */
export const formatWeekRange = (value: number): string => {
  const startOfWeekDate = startOfWeek(new Date(value), { weekStartsOn: 0 });

  if (!isValid(startOfWeekDate)) {
    return "";
  }
  const endOfWeek = addDays(startOfWeekDate, 6);
  return `${format(startOfWeekDate, "d MMM")} - ${format(endOfWeek, "d MMM")}`;
};
